// Certificado digital A1 do cliente (PO1-3008) — endpoints do backend_hub. Usar `api`
// (API_URL), não `ApiHubBackend`.
//
//   GET    /certificados/termo   → { versao, titulo, preambulo, itens: [{titulo, texto}], fecho }
//   GET    /certificados         → Certificado[] do escopo (ativos e removidos; nunca o segredo)
//   POST   /certificados         → 201 Certificado (multipart: certificado + senha + termoAceite + termoVersao)
//   DELETE /certificados/:id     → { ok: true, segredoJaAusente }
//   GET    /certificados/captura/log.csv?de&ate → CSV do log de captura (PO1-3010)
//   GET    /certificados/captura/log?de&ate&page&pageSize → { itens, total, page, pageSize } (PO1-3426)
//   GET    /certificados/captura/log/:logId → linha do log com `documentos` (PO1-3426)
//
// Todas as chamadas mandam `idEmbarcador` em query (PO1-3428): é o embarcador escolhido na
// tela, e o backend resolve o escopo por ele (sem o parâmetro, cai no comportamento antigo,
// pelo X-Active-Embarcador-Id). No POST do upload vai em query porque o multer roda depois do
// escopo no backend_hub.
//
// Certificado = { id, idEmbarcador, cnpjRaiz, cnpjCertificado, razaoSocial, numeroSerie,
//                 validadeInicio, validadeFim, ativo, dataUpload, dataRemocao, motivoRemocao,
//                 termoVersao, termoAceiteEm, cnpjsCobertos: string[], substituidos? }
//
// O .pfx e a senha passam por aqui UMA vez, no POST, e não ficam em lugar nenhum do front:
// nada de localStorage, nada de console. O interceptor de services/api.js remove o
// Content-Type quando o body é FormData e injeta o X-Active-Embarcador-Id, que é como o
// backend resolve o escopo do embarcador.
import api from "./api";

export const CERTIFICADO_EXTENSOES = ["pfx", "p12"];
export const CERTIFICADO_ACCEPT = CERTIFICADO_EXTENSOES.map((e) => "." + e).join(",");
export const CERTIFICADO_MAX_BYTES = 64 * 1024;
export const CERTIFICADO_LEGENDA = "Arquivo .pfx ou .p12 (certificado A1) · até 64 KB";

// Faixas de aviso de vencimento — as mesmas do job de alerta (PO1-3010).
export const DIAS_AVISO_VENCIMENTO = 30;

export function extensaoDe(nome) {
  const partes = String(nome || "").split(".");
  if (partes.length < 2) return "";
  const ultimo = String(partes[partes.length - 1] || "");
  return /^[A-Za-z0-9]{1,10}$/.test(ultimo) ? ultimo.toLowerCase() : "";
}

// Validação client-side antes de qualquer request (mesmas regras do backend).
export function validarArquivoCertificado(file) {
  if (!file) return { valido: false, erro: "Selecione o arquivo do certificado." };
  const nome = file.name || "(sem nome)";
  const ext = extensaoDe(nome);
  if (CERTIFICADO_EXTENSOES.indexOf(ext) === -1) {
    return { valido: false, erro: `Tipo não permitido: ${nome}. Envie o arquivo .pfx ou .p12 do certificado A1.` };
  }
  if (!file.size) return { valido: false, erro: `Arquivo vazio: ${nome}.` };
  if (file.size > CERTIFICADO_MAX_BYTES) {
    return { valido: false, erro: `Arquivo acima de 64 KB: ${nome}. Um certificado A1 tem poucos KB; confira se é o .pfx certo.` };
  }
  return { valido: true, erro: "" };
}

export async function obterTermo(idEmbarcador) {
  const { data } = await api.get("/certificados/termo", { params: { idEmbarcador } });
  return data;
}

export async function listarCertificados(idEmbarcador) {
  const { data } = await api.get("/certificados", { params: { idEmbarcador } });
  return Array.isArray(data) ? data : [];
}

export async function enviarCertificado({ file, senha, termoVersao, idEmbarcador }) {
  const form = new FormData();
  form.append("certificado", file, file.name);
  form.append("senha", senha);
  form.append("termoAceite", "true");
  form.append("termoVersao", termoVersao);
  const { data } = await api.post("/certificados", form, { params: { idEmbarcador } });
  return data;
}

export async function removerCertificado(id, idEmbarcador) {
  const { data } = await api.delete(`/certificados/${id}`, { params: { idEmbarcador } });
  return data;
}

// Log de auditoria da captura na SEFAZ (PO1-3010): CSV montado pelo backend_hub a partir do
// worker. de/ate = "YYYY-MM-DD". Dispara o download no navegador (mesmo padrão da
// exportação da Importação); devolve o nome do arquivo.
export async function exportarLogCaptura({ de, ate, idEmbarcador }) {
  const resposta = await api.get("/certificados/captura/log.csv", { params: { de, ate, idEmbarcador }, responseType: "blob" });
  const nome = `captura-sefaz-${de || "inicio"}_${ate || "hoje"}.csv`;
  const url = window.URL.createObjectURL(new Blob([resposta.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return nome;
}

// Log da captura paginado no servidor (PO1-3426). Cada item é uma consulta à SEFAZ, sem o
// array `documentos`: { _id, criadoEm, cnpj, cnpjRaiz, nsuInicial, nsuFinal, maxNSU, cStat,
// xMotivo, quantidadeDocumentos, gravados, ignorados, erros, eventosIgnorados,
// eventosGravados, duracaoMs, erro, status }.
export async function listarLogCaptura({ idEmbarcador, de, ate, page, pageSize }) {
  const { data } = await api.get("/certificados/captura/log", { params: { idEmbarcador, de, ate, page, pageSize } });
  const itens = data && Array.isArray(data.itens) ? data.itens : [];
  return {
    itens,
    total: Number(data && data.total) || itens.length,
    page: Number(data && data.page) || page || 1,
    pageSize: Number(data && data.pageSize) || pageSize || itens.length,
  };
}

// Uma linha do log com os documentos da consulta:
// documentos: [{ nsu, schema, chaveAcesso, code, message, tpEvento, s3Key }].
export async function obterLogCaptura({ idEmbarcador, logId }) {
  const { data } = await api.get(`/certificados/captura/log/${logId}`, { params: { idEmbarcador } });
  return data;
}

// Erros de blob chegam como Blob, não como JSON: lê o texto para mostrar a mensagem certa.
export async function mensagemErroBlob(error, fallback) {
  const payload = error && error.response && error.response.data;
  if (payload && typeof payload.text === "function") {
    try {
      const texto = await payload.text();
      const json = JSON.parse(texto);
      return mensagemErroCertificado({ response: { status: error.response.status, data: json } }, fallback);
    } catch (e) {
      /* não era JSON */
    }
  }
  return mensagemErroCertificado(error, fallback);
}

export function mensagemErroCertificado(error, fallback) {
  const status = error && error.response && error.response.status;
  const payload = error && error.response && error.response.data;
  const doBackend =
    payload && typeof payload === "object"
      ? (typeof payload.error === "string" && payload.error) ||
        (typeof payload.message === "string" && payload.message) ||
        ""
      : typeof payload === "string"
        ? payload
        : "";

  if (status === 503) return "Cofre de certificados não configurado no servidor. Avise o suporte.";
  if (status === 502) return doBackend || "O cofre de certificados não respondeu. Tente de novo em instantes; se persistir, avise o suporte.";
  if (status === 404) return "Certificado não encontrado no seu escopo.";
  if (status === 403) return doBackend || "Você não tem acesso ao certificado deste embarcador.";
  if (status === 409) return doBackend || "Este certificado já foi removido.";
  if (status === 413) return "Arquivo acima do limite de 64 KB.";
  return doBackend || (error && error.message) || fallback || "Não foi possível concluir a operação.";
}

export function formatarCnpj(valor) {
  const d = String(valor || "").replace(/\D/g, "");
  if (d.length !== 14) return valor || "";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function formatarRaiz(valor) {
  const d = String(valor || "").replace(/\D/g, "");
  if (d.length !== 8) return valor || "";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}`;
}

export function diasParaVencer(validadeFim, agora) {
  if (!validadeFim) return null;
  const fim = new Date(validadeFim).getTime();
  if (Number.isNaN(fim)) return null;
  const ref = agora ? new Date(agora).getTime() : Date.now();
  return Math.ceil((fim - ref) / (24 * 60 * 60 * 1000));
}

// Situação para a tela: uma só palavra por certificado, com a cor do reactstrap.
export function situacaoCertificado(cert, agora) {
  if (!cert) return { chave: "desconhecido", rotulo: "?", cor: "secondary" };
  if (!cert.ativo) {
    if (cert.motivoRemocao === "SUBSTITUIDO") return { chave: "substituido", rotulo: "Substituído", cor: "secondary" };
    return { chave: "removido", rotulo: "Removido", cor: "secondary" };
  }
  const dias = diasParaVencer(cert.validadeFim, agora);
  if (dias !== null && dias <= 0) return { chave: "vencido", rotulo: "Vencido", cor: "danger" };
  if (cert.ultimaRejeicaoCStat) {
    return { chave: "rejeitado", rotulo: `Rejeitado pela SEFAZ (${cert.ultimaRejeicaoCStat})`, cor: "danger" };
  }
  if (dias !== null && dias <= DIAS_AVISO_VENCIMENTO) {
    return { chave: "vencendo", rotulo: `Vence em ${dias} dia${dias === 1 ? "" : "s"}`, cor: "warning" };
  }
  return { chave: "ativo", rotulo: "Ativo", cor: "success" };
}
